import {
  GridList,
  GridListTile,
  GridListTileBar,
  IconButton,
  Paper
} from '@material-ui/core'
import RootRef from '@material-ui/core/RootRef'
import AddIcon from '@material-ui/icons/Add'
import ClearIcon from '@material-ui/icons/Clear'

import * as R from 'ramda'
import React, { useCallback, useEffect } from 'react'
import { useDropzone } from 'react-dropzone'
import { useFormContext, useWatch } from 'react-hook-form'
import styled from 'styled-components'

import { FileInterface } from '../../interfaces/file'
import { fileApi } from '../../services'

const StyledGridListContainer = styled.div`
  margin-bottom: 20px;
`

const FormMultiFilesDropZone = ({
  fieldName,
  accept = 'image/png, image/jpeg',
  disableForm = false,
  maxFileUploads = 5
}: {
  fieldName: string
  accept?: string
  disableForm?: boolean
  maxFileUploads?: number
}) => {
  const {
    register,
    unregister,
    setValue,
    control,
    getValues
  } = useFormContext()

  const files: FileInterface[] = useWatch({ control, name: fieldName }) || []

  const onDrop = useCallback(async (acceptedFiles: File[]) => {
    if (R.not(R.isEmpty(acceptedFiles))) {
      const form = new FormData()
      form.append('file', acceptedFiles[0])
      const { data } = await fileApi.uploadFile(form)
      setValue(fieldName, [...getValues(fieldName), data])
    }
  }, [])

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    disabled: files.length >= maxFileUploads || disableForm,
    multiple: true,
    accept,
    maxSize: 2 * 1024 * 1024,
    onDrop
  })
  const { ref, ...rootProps } = getRootProps()

  const removeFileHandler = (id: string) => {
    setValue(
      fieldName,
      R.reject((file: any) => R.equals(R.prop('fileId', file), id), files)
    )
  }

  useEffect(() => {
    register(fieldName)
    return () => unregister(fieldName)
  }, [register])

  return (
    <StyledGridListContainer>
      <RootRef rootRef={ref}>
        <GridList
          cellHeight={100}
          cols={8}
          style={{
            flexWrap: 'nowrap',
            // Promote the list into his own layer on Chrome. This cost memory but helps keeping high FPS.
            transform: 'translateZ(0)'
          }}
        >
          <Paper
            {...rootProps}
            style={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              background: disableForm ? 'grey' : 'white'
            }}
            variant="outlined"
          >
            <input {...getInputProps()} />
            {isDragActive ? (
              <p>Drop here ...</p>
            ) : (
              <AddIcon color="primary" fontSize="large" />
            )}
          </Paper>
          {files &&
            files.map((file) => (
              <GridListTile key={file.fileId} style={{ margin: '0px 10px' }}>
                <img src={file.url} alt={file.originalName} />
                <GridListTileBar
                  style={{
                    background: 'transparent'
                  }}
                  titlePosition="top"
                  actionPosition="right"
                  actionIcon={
                    !disableForm && (
                      <IconButton
                        aria-label="remove file"
                        onClick={() => removeFileHandler(file.fileId)}
                      >
                        <ClearIcon fontSize="small" />
                      </IconButton>
                    )
                  }
                />
              </GridListTile>
            ))}
        </GridList>
      </RootRef>
    </StyledGridListContainer>
  )
}

export default FormMultiFilesDropZone
